/**
 * ============================================================================
 *  TUNING CONSTANTS — the dials you'll want on real devices.
 * ============================================================================
 *
 * Everything tweakable about feel lives here so you don't have to hunt through
 * game logic. Three groups: TIMING (clock sync + playback scheduling), AUDIO
 * (synth tone shape), and ADAPTIVE (difficulty pacing + per-player assist).
 *
 * These are shared between the server (authoritative pacing) and the client
 * (rendering), so keep them serialisable plain values.
 */

/* ----------------------------------------------------------------------------
 *  TIMING — clock sync & cross-phone playback scheduling (see §11)
 * ------------------------------------------------------------------------- */
export const TIMING = {
  /** How many ping/echo round-trips a client runs on join to estimate its
   *  offset from server time. More = steadier estimate, slower join. */
  CLOCK_SAMPLES: 7,
  /** Gap between clock-sync pings (ms). */
  CLOCK_SAMPLE_GAP_MS: 120,

  /** Scheduling lead: the server schedules the FIRST playback slot this far in
   *  the future so every phone has time to receive the schedule and fire on
   *  time. Must comfortably exceed typical one-way latency + jitter.
   *  Raise if playback feels sloppy on a slow network; lower for snappier feel. */
  PLAYBACK_LEAD_MS: 1300,

  /** How long a single slot "lights up" during playback (ms). */
  SLOT_LIGHT_MS: 520,

  /** Default gap BETWEEN slots during playback (ms). The adaptive engine moves
   *  the live value between GAP_MIN and GAP_MAX around this. */
  SLOT_GAP_MS: 620,

  /** Pause after playback finishes before the response phase opens (ms). */
  PRE_RESPONSE_PAUSE_MS: 700,

  /** Base per-slot response window during the response phase (ms). Assist
   *  levels extend this (see ADAPTIVE.assistWindowBonusMs). */
  RESPONSE_WINDOW_MS: 4500,

  /** Pre-cue lead: for assisted players, how far ahead of their slot the
   *  "get ready, you're next" pulse fires (ms). */
  PRECUE_LEAD_MS: 900,
} as const;

/* ----------------------------------------------------------------------------
 *  AUDIO — warm synth tone shape (see §4). One tone per player.
 * ------------------------------------------------------------------------- */
export const AUDIO = {
  /** Oscillator type for the body of the tone. */
  WAVE: 'triangle' as OscillatorType,
  /** Attack / decay envelope (seconds). Short attack, soft decay = warm. */
  ATTACK_S: 0.012,
  DECAY_S: 0.55,
  /** Peak gain (0..1). Kept gentle — this plays right next to a child's ear. */
  PEAK_GAIN: 0.32,
  /** A subtle second oscillator a fifth up adds warmth without dissonance. */
  ADD_FIFTH: true,
  FIFTH_GAIN: 0.12,
} as const;

/* ----------------------------------------------------------------------------
 *  ADAPTIVE — shared pacing + per-player assist (see §7)
 * ------------------------------------------------------------------------- */
export const ADAPTIVE = {
  /** Starting sequence length for a fresh game. Deliberately gentle. */
  START_LENGTH: 2,

  /** Playback gap bounds (ms). Lower = faster = harder. */
  GAP_MIN_MS: 380,
  GAP_MAX_MS: 1100,
  /** Gap step applied on success (speed up) / miss (slow down). Asymmetric:
   *  we slow down faster than we speed up — bias toward "too easy". */
  GAP_SPEEDUP_STEP_MS: 45,
  GAP_SLOWDOWN_STEP_MS: 130,

  /** Per-player rolling success estimate (0..1). Starts here for everyone. */
  ASSIST_START_SCORE: 0.6,
  /** Raise assist quickly after a miss, lower slowly after sustained success
   *  (asymmetric on purpose — §7b). These are EMA weights for a miss vs hit. */
  ASSIST_EMA_ON_MISS: 0.5, // a miss pulls the estimate down hard
  ASSIST_EMA_ON_HIT: 0.12, // a hit nudges it up gently

  /** Score thresholds that map the rolling estimate -> assist level.
   *  Below LOW -> 'high', below MID -> 'light', else 'off'. */
  ASSIST_THRESHOLD_LOW: 0.4,
  ASSIST_THRESHOLD_MID: 0.72,

  /** Extra response-window time granted by assist level (ms). */
  assistWindowBonusMs: { off: 0, light: 1500, high: 4000 } as Record<AssistLevel, number>,

  /** With this assist level or higher, out-of-turn taps from OTHER players are
   *  ignored (not counted as a miss) while we wait for the right player. */
  IGNORE_WRONG_TAPS_AT: 'high' as AssistLevel,

  /** With 'high' assist, a response-window timeout is forgiven (auto-advance
   *  with a gentle re-cue) instead of failing the round. */
  FORGIVE_TIMEOUT_AT: 'high' as AssistLevel,
} as const;

export type AssistLevel = 'off' | 'light' | 'high';
